/**
 * Navigation Dropdown Module
 * Handles dropdown menu interactions for accessibility and mobile support
 */

interface NavigationConfig {
  menuSelector: string;
  dropdownSelector: string;
  submenuSelector: string;
  dropdownArrowSelector: string;
  activeClass: string;
  mobileBreakpoint: number;
  menuToggleSelector: string;
  navigationContainerSelector: string;
}

interface NavigationState {
  openDropdowns: HTMLElement[];
}

const NavigationDropdown = {
  // Configuration
  config: {
    menuSelector: '.site-navigation',
    dropdownSelector: '.has-dropdown',
    submenuSelector: '.sub-menu',
    dropdownArrowSelector: '.dropdown-arrow',
    activeClass: 'is-active',
    mobileBreakpoint: 768,
    menuToggleSelector: '.menu-toggle',
    navigationContainerSelector: '.site-navigation__primary'
  } as NavigationConfig,

  // State
  state: {
    openDropdowns: []
  } as NavigationState,

  /**
   * Initialize the navigation dropdown functionality
   */
  init(): void {
    const menu = document.querySelector(this.config.menuSelector);
    if (!menu) return;

    this.bindEvents();
    this.setupAria();
    this.setupMobileMenu();
  },

  /**
   * Bind all necessary events
   */
  bindEvents(): void {
    // Get all dropdown triggers
    const dropdowns = document.querySelectorAll<HTMLElement>(this.config.dropdownSelector);

    dropdowns.forEach(dropdown => {
      const link = dropdown.querySelector<HTMLAnchorElement>('a');
      const submenu = dropdown.querySelector<HTMLElement>(this.config.submenuSelector);

      if (!link || !submenu) return;

      // Click/touch events
      link.addEventListener('click', (e: Event) => this.handleDropdownClick(e as MouseEvent, dropdown));

      // Keyboard navigation
      dropdown.addEventListener('keydown', (e: Event) => this.handleKeyboard(e as KeyboardEvent, dropdown));

      // Mouse events for desktop
      if (!this.isMobile()) {
        dropdown.addEventListener('mouseenter', () => this.openDropdown(dropdown));
        dropdown.addEventListener('mouseleave', () => this.closeDropdown(dropdown));
      }
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e: MouseEvent) => this.handleDocumentClick(e));

    // Handle escape key globally
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
      }
    });

    // Handle window resize
    let resizeTimer: number;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        this.handleResize();
      }, 250);
    });
  },

  /**
   * Setup mobile menu functionality
   */
  setupMobileMenu(): void {
    const toggle = document.querySelector<HTMLButtonElement>(this.config.menuToggleSelector);
    const navigation = document.querySelector<HTMLElement>(this.config.navigationContainerSelector);

    if (!toggle || !navigation) return;

    // Click event for toggle button
    toggle.addEventListener('click', (e: Event) => {
      e.preventDefault();
      this.toggleMobileMenu();
    });

    // Close mobile menu on escape key
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.isMobileMenuOpen()) {
        this.closeMobileMenu();
        toggle.focus();
      }
    });

    // Close mobile menu when clicking outside
    document.addEventListener('click', (e: MouseEvent) => {
      if (this.isMobileMenuOpen() &&
          !navigation.contains(e.target as Node) &&
          !toggle.contains(e.target as Node)) {
        this.closeMobileMenu();
      }
    });
  },

  /**
   * Toggle mobile menu open/closed
   */
  toggleMobileMenu(): void {
    const toggle = document.querySelector<HTMLButtonElement>(this.config.menuToggleSelector);
    const navigation = document.querySelector<HTMLElement>(this.config.navigationContainerSelector);

    if (!toggle || !navigation) return;

    const isOpen = toggle.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  },

  /**
   * Open mobile menu
   */
  openMobileMenu(): void {
    const toggle = document.querySelector<HTMLButtonElement>(this.config.menuToggleSelector);
    const navigation = document.querySelector<HTMLElement>(this.config.navigationContainerSelector);

    if (!toggle || !navigation) return;

    toggle.setAttribute('aria-expanded', 'true');
    navigation.classList.add(this.config.activeClass);

    // Trap focus within menu
    this.trapFocus(navigation);
  },

  /**
   * Close mobile menu
   */
  closeMobileMenu(): void {
    const toggle = document.querySelector<HTMLButtonElement>(this.config.menuToggleSelector);
    const navigation = document.querySelector<HTMLElement>(this.config.navigationContainerSelector);

    if (!toggle || !navigation) return;

    toggle.setAttribute('aria-expanded', 'false');
    navigation.classList.remove(this.config.activeClass);

    // Close all dropdowns when closing mobile menu
    this.closeAllDropdowns();
  },

  /**
   * Check if mobile menu is open
   */
  isMobileMenuOpen(): boolean {
    const toggle = document.querySelector<HTMLButtonElement>(this.config.menuToggleSelector);
    return toggle !== null && toggle.getAttribute('aria-expanded') === 'true';
  },

  /**
   * Trap focus within an element
   */
  trapFocus(element: HTMLElement): void {
    const focusableElements = element.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (!firstFocusableElement || !lastFocusableElement) return;

    element.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) { // Shift + Tab
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            e.preventDefault();
          }
        } else { // Tab
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            e.preventDefault();
          }
        }
      }
    });
  },

  /**
   * Setup ARIA attributes for accessibility
   */
  setupAria(): void {
    const dropdowns = document.querySelectorAll<HTMLElement>(this.config.dropdownSelector);

    dropdowns.forEach(dropdown => {
      const link = dropdown.querySelector<HTMLAnchorElement>('a');
      const submenu = dropdown.querySelector<HTMLElement>(this.config.submenuSelector);

      if (!link || !submenu) return;

      // Add ARIA attributes
      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');

      // Generate unique ID for submenu if it doesn't have one
      if (!submenu.id) {
        submenu.id = `submenu-${Math.random().toString(36).substr(2, 9)}`;
      }

      link.setAttribute('aria-controls', submenu.id);
      submenu.setAttribute('aria-hidden', 'true');
    });
  },

  /**
   * Handle dropdown click events
   */
  handleDropdownClick(e: MouseEvent, dropdown: HTMLElement): void {
    const link = dropdown.querySelector<HTMLAnchorElement>('a');
    if (!link) return;

    const isOpen = dropdown.classList.contains(this.config.activeClass);

    // On mobile, prevent default link behavior for parent items
    if (this.isMobile() && link.getAttribute('href') === '#') {
      e.preventDefault();
    }

    // Toggle dropdown on mobile
    if (this.isMobile()) {
      e.preventDefault();
      e.stopPropagation();

      if (isOpen) {
        this.closeDropdown(dropdown);
      } else {
        // Close other dropdowns first
        this.closeAllDropdowns();
        this.openDropdown(dropdown);
      }
    }
  },

  /**
   * Handle keyboard navigation
   */
  handleKeyboard(e: KeyboardEvent, dropdown: HTMLElement): void {
    const submenu = dropdown.querySelector<HTMLElement>(this.config.submenuSelector);
    const link = dropdown.querySelector<HTMLAnchorElement>('a');

    if (!submenu || !link) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        if (document.activeElement === link) {
          e.preventDefault();
          const isOpen = dropdown.classList.contains(this.config.activeClass);
          if (isOpen) {
            this.closeDropdown(dropdown);
          } else {
            this.openDropdown(dropdown);
          }
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (dropdown.classList.contains(this.config.activeClass)) {
          // Focus first item in submenu
          const firstLink = submenu.querySelector<HTMLAnchorElement>('a');
          if (firstLink) firstLink.focus();
        } else {
          this.openDropdown(dropdown);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (dropdown.classList.contains(this.config.activeClass)) {
          // Focus last item in submenu
          const links = submenu.querySelectorAll<HTMLAnchorElement>('a');
          if (links.length > 0) {
            links[links.length - 1].focus();
          }
        }
        break;

      case 'Escape':
        this.closeDropdown(dropdown);
        link.focus();
        break;
    }

    // Handle navigation within submenu
    if (submenu.contains(document.activeElement)) {
      const items = Array.from(submenu.querySelectorAll<HTMLAnchorElement>('a'));
      const currentIndex = items.indexOf(document.activeElement as HTMLAnchorElement);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (currentIndex < items.length - 1) {
            items[currentIndex + 1].focus();
          } else {
            items[0].focus(); // Loop to first
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (currentIndex > 0) {
            items[currentIndex - 1].focus();
          } else {
            items[items.length - 1].focus(); // Loop to last
          }
          break;

        case 'Tab':
          // Close dropdown when tabbing out
          if (e.shiftKey && currentIndex === 0) {
            setTimeout(() => this.closeDropdown(dropdown), 100);
          } else if (!e.shiftKey && currentIndex === items.length - 1) {
            setTimeout(() => this.closeDropdown(dropdown), 100);
          }
          break;
      }
    }
  },

  /**
   * Handle clicks outside of dropdowns
   */
  handleDocumentClick(e: MouseEvent): void {
    const dropdowns = document.querySelectorAll<HTMLElement>(this.config.dropdownSelector);

    dropdowns.forEach(dropdown => {
      if (!dropdown.contains(e.target as Node) && dropdown.classList.contains(this.config.activeClass)) {
        this.closeDropdown(dropdown);
      }
    });
  },

  /**
   * Open a specific dropdown
   */
  openDropdown(dropdown: HTMLElement): void {
    const link = dropdown.querySelector<HTMLAnchorElement>('a');
    const submenu = dropdown.querySelector<HTMLElement>(this.config.submenuSelector);

    if (!link || !submenu) return;

    dropdown.classList.add(this.config.activeClass);
    link.setAttribute('aria-expanded', 'true');
    submenu.setAttribute('aria-hidden', 'false');

    // Add to state
    if (!this.state.openDropdowns.includes(dropdown)) {
      this.state.openDropdowns.push(dropdown);
    }
  },

  /**
   * Close a specific dropdown
   */
  closeDropdown(dropdown: HTMLElement): void {
    const link = dropdown.querySelector<HTMLAnchorElement>('a');
    const submenu = dropdown.querySelector<HTMLElement>(this.config.submenuSelector);

    if (!link || !submenu) return;

    dropdown.classList.remove(this.config.activeClass);
    link.setAttribute('aria-expanded', 'false');
    submenu.setAttribute('aria-hidden', 'true');

    // Remove from state
    const index = this.state.openDropdowns.indexOf(dropdown);
    if (index > -1) {
      this.state.openDropdowns.splice(index, 1);
    }
  },

  /**
   * Close all open dropdowns
   */
  closeAllDropdowns(): void {
    const dropdowns = document.querySelectorAll<HTMLElement>(`${this.config.dropdownSelector}.${this.config.activeClass}`);
    dropdowns.forEach(dropdown => this.closeDropdown(dropdown));
  },

  /**
   * Handle window resize events
   */
  handleResize(): void {
    // Close all dropdowns on resize to prevent layout issues
    this.closeAllDropdowns();

    // Close mobile menu on resize
    if (!this.isMobile()) {
      this.closeMobileMenu();
    }
  },

  /**
   * Check if current viewport is mobile
   */
  isMobile(): boolean {
    return window.innerWidth < this.config.mobileBreakpoint;
  }
};

// Export for use in other modules
export default NavigationDropdown;
